import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPlanning, rechercherPlanning } from '../services/api';
import { useAuth } from '../services/AuthContext';
import BarreNav from '../components/BarreNav';
import { couleurTypo, styleTypo } from '../services/typologies';

const JOURS = ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'];
const MOIS = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

function getDateRange(center, count = 8) {
  const dates = [];
  for (let i = -1; i < count - 1; i++) {
    const d = new Date(center);
    d.setDate(center.getDate() + i);
    dates.push(d);
  }
  return dates;
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function toDateStr(d) {
  // Format YYYY-MM-DD en heure locale pour éviter les décalages UTC
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const STAGE_STYLES = {
  'CLEARED':                        { bg: 'var(--succes-fond)', color: 'var(--succes-texte)', border: 'var(--succes-bord)' },
  'FAILED':                         { bg: 'var(--danger-fond)', color: 'var(--danger-texte)', border: 'var(--danger-bord)' },
  'Planifiée':                      { bg: 'var(--info-fond)', color: 'var(--accent)', border: 'var(--info-bord)' },
  'CR non conforme':                { bg: 'var(--attention-fond)', color: 'var(--attention-texte)', border: 'var(--attention-bord)' },
  'En attente validation DEVIS':    { bg: 'var(--attention-fond)', color: 'var(--attention-texte)', border: 'var(--attention-bord)' },
  'RDVNH':                          { bg: 'var(--danger-fond)', color: 'var(--danger-texte)', border: 'var(--danger-bord)' },
  'En attente validation DOSTEC/ARC': { bg: 'var(--violet-fond)', color: 'var(--violet-texte)', border: 'var(--violet-bord)' },
  'CLEARED suite reprise':          { bg: 'var(--succes-fond)', color: 'var(--succes-texte)', border: 'var(--succes-bord)' },
  'CRi non cloturé':                { bg: 'var(--danger-fond)', color: 'var(--danger-texte)', border: 'var(--danger-bord)' },
  'IAT':                            { bg: 'var(--danger-fond)', color: 'var(--danger-texte)', border: 'var(--danger-bord)' },
  'En attente validation Admin':    { bg: 'var(--rose-fond)', color: 'var(--rose-texte)', border: 'var(--rose-bord)' },
};

function StageLabel({ stage }) {
  const style = STAGE_STYLES[stage] || { bg: 'var(--surface-3)', color: 'var(--texte-doux)', border: 'var(--bord)' };
  return (
    <div style={{ marginTop: 6 }}>
      <span style={{
        fontSize: 11, fontWeight: 600, padding: '3px 10px',
        borderRadius: 20, border: `1px solid ${style.border}`,
        background: style.bg, color: style.color,
      }}>
        {stage}
      </span>
    </div>
  );
}

/**
 * Le serveur joint désormais le statut du compte rendu à chaque intervention :
 * une journée de dix rendez-vous ne déclenche plus dix requêtes, et les badges
 * apparaissent avec la carte au lieu de surgir après coup. L'appel reste en
 * repli pour les appelants qui ne fournissent pas l'information.
 */
function CRStatut({ dossierId, refWorkOrder, statut }) {
  const fourni = statut !== undefined;
  const [crStatus, setCrStatus] = useState(fourni ? statut : null);

  useEffect(() => {
    if (fourni) { setCrStatus(statut); return; }
    const token = localStorage.getItem('token');
    const woParam = refWorkOrder ? `?ref_work_order=${encodeURIComponent(refWorkOrder)}` : '';
    fetch(`/api/cr/${dossierId}${woParam}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        if (!data.cr || !data.cr.final) { setCrStatus(false); return; }
        setCrStatus(data.cr.admin_status || 'pending');
      })
      .catch(() => setCrStatus(false));
  }, [dossierId, refWorkOrder, statut, fourni]);

  if (crStatus === null) return null;
  if (!crStatus) return (
    <span style={{
      background: 'var(--info-fond)', color: 'var(--accent)', border: '1px solid var(--info-bord)',
      borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>À faire</span>
  );
  if (crStatus === 'validated') return (
    <span style={{
      background: 'var(--succes-fond)', color: 'var(--succes-texte)', border: '1px solid var(--succes-bord)',
      borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>CR validé ✓</span>
  );
  if (crStatus === 'rejected') return (
    <span style={{
      background: 'var(--danger-fond)', color: 'var(--danger-texte)', border: '1px solid var(--danger-bord)',
      borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>CR rejeté ✕</span>
  );
  // pending
  return (
    <span style={{
      background: 'var(--succes-fond)', color: 'var(--succes-texte)', border: '1px solid var(--succes-bord)',
      borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700,
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>CR envoyé ✓</span>
  );
}

const RED_STAGES = ['RDVNH', 'CRi non cloturé', 'CRI non cloturé'];

const MOIS_COURTS = ['jan.','fév.','mars','avr.','mai','juin','juil.','août','sep.','oct.','nov.','déc.'];

/** « 2026-09-21 » → « sam. 21 sep. 2026 » */
function dateCourte(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  const dt = new Date(y, m - 1, d);
  return `${JOURS[dt.getDay()]}. ${d} ${MOIS_COURTS[m - 1]} ${y}`;
}

/**
 * Carte d'une intervention, commune au planning du jour et aux résultats de
 * recherche. Ces derniers portent des dates variées : la date n'est affichée
 * que là, le planning ayant déjà la sienne en en-tête.
 */
function CarteIntervention({ item, onClick, avecDate = false }) {
  const isRed = RED_STAGES.includes(item.stage);
  const teintes = couleurTypo(item.typo_raccordement);
  return (
    <div onClick={onClick} style={{
      background: isRed ? 'var(--danger-fond)' : 'var(--surface)',
      borderRadius: 14, padding: '13px 15px 13px 13px',
      border: `0.5px solid ${isRed ? 'var(--danger-bord)' : 'var(--bord)'}`,
      // Liseré de famille : la nature de l'intervention se lit avant le texte,
      // ce qui permet de balayer une journée d'un coup d'œil.
      borderLeft: `5px solid ${teintes.texte}`,
      cursor: 'pointer',
      boxShadow: 'var(--ombre)',
    }}>
      {/* Ligne 1 : l'heure d'abord — c'est par elle qu'on lit une journée */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 5, gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
          {item.heure_intervention && (
            <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--texte)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
              {item.heure_intervention}
            </span>
          )}
          {avecDate && (
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.03em' }}>
              {dateCourte(item.date_intervention) || 'Non planifiée'}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
          <CRStatut dossierId={item.id} refWorkOrder={item.ref_work_order} statut={item.statut_cr} />
          {item.stage && <StageLabel stage={item.stage} />}
        </div>
      </div>

      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--texte)', lineHeight: 1.3, marginBottom: 4 }}>
        {item.client_final || item.nom}
      </div>

      <div style={{ fontSize: 12, color: 'var(--texte-doux)', marginBottom: 7 }}>
        {[item.numero, item.rue, item.code_postal, item.ville].filter(Boolean).join(', ').toUpperCase()}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {item.typo_raccordement && (
          <span style={styleTypo(item.typo_raccordement)}>{item.typo_raccordement}</span>
        )}
        {item.devis_forfaitaire && (
          <span style={{ fontSize: 11, background: 'var(--attention-fond)', color: 'var(--attention-texte)', border: '1px solid var(--attention-bord)', borderRadius: 6, padding: '2px 8px', fontWeight: 700 }}>
            💰 Devis
          </span>
        )}
        {avecDate && item.ref_commande && (
          <span style={{ fontSize: 11, color: 'var(--texte-doux)', fontFamily: 'ui-monospace, monospace' }}>
            {item.ref_commande}
          </span>
        )}
      </div>
    </div>
  );
}

export default function Planning() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const datePickerRef = useRef(null);
  const [today] = useState(() => new Date());
  const [centerDate, setCenterDate] = useState(() => new Date());
  // On stocke la date sélectionnée comme string YYYY-MM-DD pour éviter tout bug de comparaison
  const [selectedStr, setSelectedStr] = useState(() => toDateStr(new Date()));
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({});
  const dates = getDateRange(centerDate);

  // ── Recherche ──────────────────────────────────────────────────────────────
  const [requete, setRequete] = useState('');
  const [resultats, setResultats] = useState(null);   // null tant qu'aucune recherche n'a abouti
  const [chercheEnCours, setChercheEnCours] = useState(false);
  const [erreurRecherche, setErreurRecherche] = useState('');
  const enRecherche = requete.trim().length >= 2;

  // Frappe au clavier sur mobile : on attend une pause avant d'interroger le
  // serveur, et on annule la requête précédente pour éviter qu'une réponse
  // tardive n'écrase une plus récente.
  useEffect(() => {
    const q = requete.trim();
    if (q.length < 2) { setResultats(null); setChercheEnCours(false); setErreurRecherche(''); return; }
    const ctrl = new AbortController();
    setChercheEnCours(true);
    setErreurRecherche('');
    const t = setTimeout(() => {
      rechercherPlanning(q, { signal: ctrl.signal })
        .then(d => { setResultats(d); setChercheEnCours(false); })
        .catch(e => {
          if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return;
          setErreurRecherche("La recherche n'a pas abouti. Réessayez dans un instant.");
          setResultats(null);
          setChercheEnCours(false);
        });
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [requete]);

  // Charger les counts quand la fenêtre de dates change
  useEffect(() => {
    dates.forEach(d => {
      const ds = toDateStr(d);
      if (counts[ds] !== undefined) return; // déjà chargé
      getPlanning(ds)
        .then(data => {
          const n = (data.interventions || []).length;
          setCounts(prev => ({ ...prev, [ds]: n }));
        })
        .catch(() => {});
    });
  }, [centerDate]);

  // Charger les interventions quand la date sélectionnée change
  useEffect(() => {
    setLoading(true);
    setInterventions([]);
    getPlanning(selectedStr)
      .then(data => setInterventions(data.interventions || []))
      .catch(() => setInterventions([]))
      .finally(() => setLoading(false));
  }, [selectedStr]);

  // Reconstruire la date sélectionnée pour l'affichage
  const [sy, sm, sd] = selectedStr.split('-').map(Number);
  const selectedDate = new Date(sy, sm - 1, sd);
  const jourLabel = `${JOURS[selectedDate.getDay()]} ${selectedDate.getDate()} ${MOIS[selectedDate.getMonth()]} ${selectedDate.getFullYear()}`;

  return (
    <div className="app-shell">
      <div className="topbar">
        <div className="topbar-title">Mon planning</div>
        <div style={{ fontSize: 13, color: 'var(--texte-doux)' }}>{user?.prenom} {user?.nom}</div>
      </div>

      <div className="content">
        {/* Recherche — porte sur les interventions du technicien, toutes dates */}
        <div style={{ position: 'relative', marginBottom: 12 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 15, pointerEvents: 'none' }}>🔍</span>
          <input
            type="search"
            value={requete}
            onChange={e => setRequete(e.target.value)}
            placeholder="Client, réf. O ou W, adresse, ville…"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '11px 38px 11px 36px',
              fontSize: 16, borderRadius: 10, border: '1px solid var(--bord-fort)',
              background: 'var(--surface)', color: 'var(--texte)', outline: 'none',
              WebkitAppearance: 'none',
            }}
          />
          {requete && (
            <button
              onClick={() => setRequete('')}
              aria-label="Effacer la recherche"
              style={{
                position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                border: 'none', background: 'none', fontSize: 18, color: 'var(--texte-doux)',
                cursor: 'pointer', padding: '4px 8px', lineHeight: 1,
              }}
            >×</button>
          )}
        </div>

        {enRecherche ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--texte-doux)', marginBottom: 14 }}>
              {chercheEnCours
                ? 'Recherche…'
                : erreurRecherche
                  ? ''
                  : `${resultats?.count ?? 0} résultat${(resultats?.count ?? 0) !== 1 ? 's' : ''}` +
                    (resultats?.tronque ? ` — les ${resultats.interventions.length} plus récents` : '')}
            </div>

            {erreurRecherche ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--danger-texte)', fontSize: 14 }}>{erreurRecherche}</div>
            ) : chercheEnCours && !resultats ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--texte-doux)' }}>Recherche…</div>
            ) : !resultats?.interventions?.length ? (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--texte-faible)' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                <div>Aucune intervention ne correspond</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {resultats.interventions.map(item => (
                  <CarteIntervention
                    key={item.id}
                    item={item}
                    avecDate
                    onClick={() => navigate(`/dossier/${item.id}`)}
                  />
                ))}
              </div>
            )}
            <div style={{ height: 16 }} />
          </>
        ) : (
        <>
        {/* Sélecteur de dates */}
        {/* Ligne mois/année + bouton calendrier */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--texte)' }}>
            {MOIS[centerDate.getMonth()]} {centerDate.getFullYear()}
          </span>
          {/* Bouton + input superposé — le tap iOS touche l'input directement */}
          <div style={{ position: 'relative', display: 'inline-flex' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--info-fond)', border: '1px solid var(--info-bord)', borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 600, color: 'var(--accent)', pointerEvents: 'none', userSelect: 'none' }}>
              <span style={{ fontSize: 14 }}>📅</span> Aller à une date
            </div>
            <input
              ref={datePickerRef}
              type="date"
              value={selectedStr}
              onChange={e => {
                const val = e.target.value;
                if (!val) return;
                const [y, m, d] = val.split('-').map(Number);
                const picked = new Date(y, m - 1, d);
                setCenterDate(picked);
                setSelectedStr(val);
              }}
              style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', height: '100%', cursor: 'pointer', fontSize: 16 }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
          <button onClick={() => setCenterDate(d => { const n = new Date(d); n.setDate(n.getDate() - 7); return n; })} style={{ background: 'none', border: '1px solid var(--bord-fort)', borderRadius: 8, padding: '4px 10px', fontSize: 16, cursor: 'pointer', color: 'var(--accent)', flexShrink: 0 }}>‹</button>
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', flex: 1, padding: '4px 0', scrollbarWidth: 'none' }}>
          {dates.map((d, i) => {
            const ds = toDateStr(d);
            const isSelected = ds === selectedStr;
            const isToday = isSameDay(d, today);
            const count = counts[ds];
            return (
              <div key={i} onClick={() => setSelectedStr(ds)} style={{
                minWidth: 52, textAlign: 'center', padding: '8px 6px', borderRadius: 12, cursor: 'pointer',
                background: isSelected ? 'var(--accent-plein)' : 'transparent',
                border: isToday && !isSelected ? '1.5px solid var(--accent)' : '1.5px solid transparent',
                transition: 'all .15s', flexShrink: 0,
              }}>
                <div style={{ fontSize: 11, color: isSelected ? 'rgba(255,255,255,0.7)' : '#8e8e93', marginBottom: 2 }}>
                  {JOURS[d.getDay()]}
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: isSelected ? '#fff' : 'var(--texte)', lineHeight: 1 }}>
                  {d.getDate()}
                </div>
                {count > 0 && (
                  <div style={{ fontSize: 10, color: isSelected ? 'rgba(255,255,255,0.8)' : '#185FA5', marginTop: 3, fontWeight: 600 }}>
                    {count}
                  </div>
                )}
              </div>
            );
          })}
          </div>
          <button onClick={() => setCenterDate(d => { const n = new Date(d); n.setDate(n.getDate() + 7); return n; })} style={{ background: 'none', border: '1px solid var(--bord-fort)', borderRadius: 8, padding: '4px 10px', fontSize: 16, cursor: 'pointer', color: 'var(--accent)', flexShrink: 0 }}>›</button>
        </div>

        <div style={{ fontSize: 13, color: 'var(--texte-doux)', marginBottom: 14 }}>
          {loading
            ? 'Chargement…'
            : <>
                {interventions.length} intervention{interventions.length !== 1 ? 's' : ''}
                {(() => {
                  // Avancement du jour : compté sur les comptes rendus déposés,
                  // que le serveur joint désormais à chaque intervention.
                  const faites = interventions.filter(i => i.statut_cr).length;
                  return interventions.length > 0 && (
                    <span style={{ color: faites === interventions.length ? 'var(--succes-texte)' : 'var(--texte-doux)', fontWeight: 600 }}>
                      {' · '}{faites}/{interventions.length} clôturée{interventions.length > 1 ? 's' : ''}
                    </span>
                  );
                })()}
              </>} — {jourLabel}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--texte-doux)' }}>Chargement…</div>
        ) : interventions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--texte-faible)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📅</div>
            <div>Aucune intervention ce jour</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[...interventions]
              .sort((a, b) => (a.heure_intervention || '').localeCompare(b.heure_intervention || ''))
              .map((item, i) => (
                <CarteIntervention
                  key={item.id || i}
                  item={item}
                  onClick={() => navigate(`/dossier/${item.id}`)}
                />
              ))}
          </div>
        )}
        <div style={{ height: 16 }} />
        </>
        )}
      </div>

      <BarreNav actif="planning" />
    </div>
  );
}
